import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Col, Checkbox, Collapse, Descriptions, Empty, Input, Modal, Row, Select, Space, Spin, Statistic, Table, Tag, Typography, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import axios from "../api/axios";
import ManualTourEditor from "../components/ManualTourEditor";
import { mapLink, matchesResultTour } from "../utils/mapTours";
import { createTourDraft, changedDraftBuses, changedDraftDirections, unassignedDraftRides } from "../utils/manualTourEditing";
import { retainedDrafts, draftWorkspaceKey } from "../utils/tourEditSession";
import { scrollToTarget } from "../utils/scrollToTarget";
const { Title, Text } = Typography;
const DAYS = { MONDAY: "Montag", TUESDAY: "Dienstag", WEDNESDAY: "Mittwoch", THURSDAY: "Donnerstag", FRIDAY: "Freitag" };
const number = value => Number(value || 0).toLocaleString("de-DE", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const euro = value => `${number(value)} €`;
const time = value => value ? (Array.isArray(value) ? value.slice(0, 2).map(v => String(v).padStart(2, "0")).join(":") : String(value).slice(0, 5)) : "–";
const date = value => value ? new Date(`${value}T12:00:00`).toLocaleDateString("de-DE") : "–";

// Compare the displayed schedule, keeping different times, stops and costs separate.
export function groupToursByBus(tours) {
  const buses = new Map();
  for (const tour of tours) {
    const busKey = String(tour.busId);
    if (!buses.has(busKey)) buses.set(busKey, { key: busKey, busId: tour.busId,
      busName: tour.busName, depotName: tour.depotAddress || tour.depotName,
      manuallyEdited: tour.manuallyEdited,
      seats: tour.seats, wheelchairSeats: tour.wheelchairSeats, electricWheelchairSeats: tour.electricWheelchairSeats,
      tours: [], patterns: new Map() });
    const bus = buses.get(busKey);
    bus.tours.push(tour);
    const signature = JSON.stringify([
      tour.richtung, tour.depotAddress || tour.depotName, tour.startTime, tour.endTime,
      tour.distanceKm, tour.durationHours, tour.totalCost,
      tour.timeWindowFeasible, tour.timeWindowViolations, tour.weeklyPlanningNotes,
      (tour.stops || []).map(stop => [stop.type, stop.passengerId, stop.passengerName,
        stop.address || stop.location?.name, stop.location?.lat, stop.location?.lon, stop.scheduledTime,
        stop.timeWindowFeasible, stop.timeWindowViolation, stop.herausforderndesVerhalten,
        stop.nichtZusammenMitFahrgast, stop.umsteigezeitMinuten]),
    ]);
    if (!bus.patterns.has(signature)) bus.patterns.set(signature, {
      ...tour, key: `${busKey}-${bus.patterns.size}`, days: [],
    });
    const pattern = bus.patterns.get(signature);
    if (!pattern.days.includes(tour.tag)) pattern.days.push(tour.tag);
  }
  return [...buses.values()].sort((a, b) => String(a.busName || a.busId)
    .localeCompare(String(b.busName || b.busId), "de", { numeric: true }))
    .map(bus => ({ ...bus, patterns: [...bus.patterns.values()].map(pattern => ({
      ...pattern, days: pattern.days.sort((a, b) => Object.keys(DAYS).indexOf(a) - Object.keys(DAYS).indexOf(b)),
    })) }));
}

const weekdays = days => days.length === 5 && Object.keys(DAYS).every(day => days.includes(day))
  ? "Montag bis Freitag" : days.map(day => DAYS[day] || day).join(", ");

export default function ResultsPage() {
  const location = useLocation();
  const [linkParams] = useSearchParams();
  const targetBus = linkParams.get("bus");
  const targetDirection = linkParams.get("direction");
  const targetDay = linkParams.get("day");
  const targetCard = useRef(null);
  const scrolledTarget = useRef(null);
  const [activeBusKeys, setActiveBusKeys] = useState([]);
  const isTargetTour = tour => matchesResultTour(tour, { bus: targetBus, direction: targetDirection, day: targetDay });
  const [result, setResult] = useState(null);
  const [editor, setEditor] = useState(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const workspaceKey = draftWorkspaceKey();
  const [session, setSession] = useState(() => retainedDrafts.get(workspaceKey) || null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [discardOpen, setDiscardOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [allowUnassigned, setAllowUnassigned] = useState(false);
  const changedBuses = useMemo(() => session ? changedDraftBuses(session.draft, createTourDraft(session.data.result.tours)) : [], [session]);
  const changedDirections = useMemo(() => session ? changedDraftDirections(session.draft, createTourDraft(session.data.result.tours)) : [], [session]);
  const pending = changedBuses.length > 0;
  useEffect(() => {
    if (pending) retainedDrafts.set(workspaceKey, session);
    else retainedDrafts.delete(workspaceKey);
  }, [pending, session, workspaceKey]);
  const unassigned = useMemo(() => session ? unassignedDraftRides(session.data, session.draft) : [], [session]);
  useEffect(() => {
    const preventClose = event => { if (pending || saving || restoring) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", preventClose);
    return () => window.removeEventListener("beforeunload", preventClose);
  }, [pending, saving, restoring]);
  const openEditor = async initialTour => {
    if (session) { setEditor({ ...session, initialTour }); return; }
    setEditorLoading(true);
    try {
      const response = await axios.get("/saved-result/edit-data");
      const nextSession = { data: response.data, draft: createTourDraft(response.data.result.tours) };
      setResult(response.data.result);
      setSession(nextSession);
      setEditor({ ...nextSession, initialTour });
    } catch (failure) {
      message.error(failure.response?.data?.message || "Die Touren konnten nicht zum Bearbeiten geladen werden.");
    } finally { setEditorLoading(false); }
  };
  const calculateChanges = async () => {
    setSaving(true); setSaveError("");
    try {
      const response = await axios.post("/saved-result/recalculate", {
        completedAt: session.data.result.completedAt,
        tours: session.draft.filter(tour => tour.passengerIds.length > 0),
      });
      try {
        localStorage.setItem("routing.cachedTours", JSON.stringify(response.data.tours));
        localStorage.setItem("routing.cachedReportContext", JSON.stringify(response.data.context));
      } catch { /* The saved server result remains available. */ }
      setResult(response.data); setSession(null); setAllowUnassigned(false);
      message.success("Alle Änderungen wurden berechnet und gespeichert. Fahrpläne und Downloads sind aktualisiert.");
    } catch (failure) {
      setSaveError(failure.response?.data?.message || "Die Berechnung ist fehlgeschlagen. Deine gesammelten Änderungen bleiben im Entwurf erhalten.");
    } finally { setSaving(false); }
  };
  const restoreOptimized = async () => {
    setRestoring(true); setSaveError("");
    try {
      const response = await axios.post("/saved-result/restore-optimized", { completedAt: result.completedAt });
      try {
        localStorage.setItem("routing.cachedTours", JSON.stringify(response.data.tours));
        localStorage.setItem("routing.cachedReportContext", JSON.stringify(response.data.context));
      } catch { /* The restored server result remains available. */ }
      setResult(response.data); setSession(null); setEditor(null); setAllowUnassigned(false);
      setRestoreOpen(false); setDay(undefined); setDirection(undefined); setQuery("");
      message.success("Die letzte optimierte Planung wurde wiederhergestellt – einschließlich Übersicht und Downloads.");
    } catch (failure) {
      setSaveError(failure.response?.data?.message || "Die Wiederherstellung ist fehlgeschlagen. Dein bisheriger Stand bleibt erhalten.");
    } finally { setRestoring(false); }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [day, setDay] = useState();
  const [direction, setDirection] = useState();
  const [query, setQuery] = useState("");
  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const response = await axios.get("/saved-result");
      setResult(response.status === 204 ? null : response.data);
      const remembered = retainedDrafts.get(workspaceKey);
      if (remembered && response.data?.completedAt === remembered.data.result.completedAt) setSession(remembered);
      else {
        setSession(null); retainedDrafts.delete(workspaceKey);
        if (remembered) message.warning("Die gespeicherte Planung wurde inzwischen geändert. Bitte die Anpassungen auf dem aktuellen Stand neu vornehmen.");
      }
      setSaveError(""); setAllowUnassigned(false);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [workspaceKey]);
  useEffect(() => { load(); }, [load]);
  const download = async (path, filename) => {
    setDownloading(path);
    try {
      const response = await axios.get(path, { responseType: "blob" });
      if (response.status === 204) { message.info("Keine Download-Datei vorhanden."); return; }
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { message.error("Download fehlgeschlagen. Bitte erneut versuchen."); }
    finally { setDownloading(null); }
  };
  const tours = useMemo(() => (result?.tours || [])
    .map((tour, index) => ({ ...tour, key: `${tour.busId}-${tour.tag}-${tour.richtung}-${index}` }))
    .filter(tour => (!day || tour.tag === day) && (!direction || tour.richtung === direction)
      && `${tour.busName || ""} ${tour.busId} ${tour.depotName || ""} ${(tour.stops || []).map(stop => stop.passengerName || "").join(" ")}`.toLocaleLowerCase("de").includes(query.toLocaleLowerCase("de")))
    .sort((a, b) => Object.keys(DAYS).indexOf(a.tag) - Object.keys(DAYS).indexOf(b.tag)
      || String(a.busId).localeCompare(String(b.busId), "de", { numeric: true }) || a.richtung.localeCompare(b.richtung)), [result, day, direction, query]);
  const buses = useMemo(() => groupToursByBus(tours), [tours]);
  useEffect(() => {
    if (!targetBus) { setActiveBusKeys([]); scrolledTarget.current = null; return; }
    if (!result) return;
    setQuery(""); setDay(undefined); setDirection(undefined);
    const exists = (result.tours || []).some(tour => String(tour.busId) === targetBus);
    setActiveBusKeys(exists ? [targetBus] : []);
  }, [targetBus, targetDirection, targetDay, result]);
  useEffect(() => {
    if (!targetBus || !activeBusKeys.includes(targetBus) || loading) return;
    const key = [location.key, targetBus, targetDirection, targetDay, result?.completedAt].join(":");
    if (scrolledTarget.current === key) return;
    return scrollToTarget(() => targetCard.current, () => { scrolledTarget.current = key; });
  }, [location.key, targetBus, targetDirection, targetDay, activeBusKeys, loading, result?.completedAt]);
  const stopColumns = [
    { title: "Nr.", render: (_, stop, index) => index + 1, width: 60 },
    { title: "Halt", dataIndex: "type", render: value => ({ DEPOT: "Depot", PICKUP: "Abholung", DROPOFF: "Ausstieg" }[value] || value) },
    { title: "Fahrgast", dataIndex: "passengerName", render: value => value || "–" },
    { title: "Adresse", render: (_, stop) => stop.address || stop.location?.name || "–" },
    { title: "Uhrzeit", dataIndex: "scheduledTime", render: time },
    { title: "Hinweise", render: (_, stop) => <Space direction="vertical" size={0}>
      {stop.timeWindowViolation && <Text type="warning">{stop.timeWindowViolation}</Text>}
      {stop.herausforderndesVerhalten && <Text>Herausforderndes Verhalten</Text>}
      {stop.nichtZusammenMitFahrgast && <Text>Nicht zusammen mit: {stop.nichtZusammenMitFahrgast}</Text>}
      {stop.umsteigezeitMinuten > 0 && <Text>Umsteigezeit: {stop.umsteigezeitMinuten} Min.</Text>}
    </Space> },
  ];
  const summary = result?.summary || {};
  const period = result?.periodSummary || {};
  return <Space direction="vertical" size="large" style={{ width: "100%" }}>
    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12 }}>
      <div><Title level={2} style={{ margin: 0 }}>Ergebnisse</Title><Text type="secondary">Die letzte erfolgreich gespeicherte Berechnung</Text></div>
      <Space wrap>
        <Button type="primary" loading={saving} disabled={restoring || loading || !pending || (unassigned.length > 0 && !allowUnassigned)} onClick={calculateChanges}>Anpassungen berechnen</Button>
        {result?.canRestoreOptimized && <Button disabled={saving || editorLoading || loading} loading={restoring}
          onClick={() => setRestoreOpen(true)}>Optimierte Planung wiederherstellen</Button>}
      </Space>
    </div>
    {pending && <Alert type="warning" showIcon message={`Änderungen an ${changedBuses.length} ${changedBuses.length === 1 ? "Bus" : "Bussen"} gesammelt – noch nicht berechnet`}
      description={<Space direction="vertical" style={{ width: "100%" }}>
        <Text>Du kannst nacheinander weitere Busse bearbeiten. Die angezeigten Kennzahlen, Fahrpläne und Downloads zeigen bis zur gemeinsamen Berechnung den zuletzt gespeicherten Stand.</Text>
        <Text>Betroffen: {changedBuses.map(id => session.data.buses.find(bus => String(bus.id) === id)?.name || `Bus ${id}`).join(", ")}</Text>
        {unassigned.length > 0 && <>
          <details><summary>{unassigned.length} nicht eingeplante Fahrgast-Fahrten anzeigen</summary>{unassigned.map((ride, i) => <div key={i}>{ride.name} · {DAYS[ride.day]} · {ride.direction === "HIN" ? "Hin" : "Rück"}</div>)}</details>
          <Checkbox checked={allowUnassigned} disabled={saving} onChange={event => setAllowUnassigned(event.target.checked)}>Diese Fahrten sollen bewusst nicht eingeplant werden.</Checkbox>
        </>}
        <Button danger disabled={saving || restoring} onClick={() => setDiscardOpen(true)}>Alle Änderungen verwerfen</Button>
        {saving && <Text>Alle Anpassungen werden gemeinsam berechnet und die Downloads neu erstellt. Bitte die Seite geöffnet lassen.</Text>}
      </Space>} />}
    {saveError && <Alert type="error" showIcon message={saveError} />}
    {error ? <Alert type="error" showIcon message="Ergebnisse konnten nicht geladen werden." action={<Button onClick={load}>Erneut versuchen</Button>} />
      : loading ? <div style={{ padding: 60, textAlign: "center" }}><Spin size="large" /></div>
      : !result ? <Empty description="Noch keine gespeicherte Berechnung vorhanden."><Link to="/app/routes"><Button type="primary">Routen berechnen</Button></Link></Empty>
      : <>
        {result.manuallyEdited && <Tag color="blue">Manuell angepasst und neu berechnet</Tag>}
        {result.restoredAt && !result.manuallyEdited && <Tag color="green">Optimierte Planung wiederhergestellt</Tag>}
        {result.manuallyEdited && result.canRestoreOptimized === false && <Alert type="info" showIcon
          message="Für diesen älteren Stand ist die ursprüngliche optimierte Planung nicht mehr verfügbar. Neue Optimierungen werden künftig separat zur Wiederherstellung aufbewahrt." />}
        <Alert type="info" showIcon message={`${date(result.context.start)} bis ${date(result.context.end)} · Bundesland: ${result.context.bundesland === "NONE" ? "ohne Feiertagsabzug" : result.context.bundesland}`}
          description={<>Gespeichert am {new Date(result.completedAt).toLocaleString("de-DE")}. Eine neue erfolgreiche Berechnung ersetzt dieses Ergebnis. Berechnungsbasis: {result.context.calculationBasis === "OCCUPIED_ONLY" ? "nur besetzte Strecke" : "Depot bis Depot"}.</>} />
        <Card title="Gesamtübersicht · gewählter Zeitraum">
          <Row gutter={[24, 20]}>
            <Col xs={24} sm={12} xl={6}><Statistic title="Gesamtkosten" value={euro(period.cost)} /></Col>
            <Col xs={24} sm={12} xl={6}><Statistic title="Gesamtstrecke" value={`${number(period.km)} km`} /></Col>
            <Col xs={24} sm={12} xl={6}><Statistic title="Fahrtzeit" value={`${number(period.hours)} h`} /></Col>
            <Col xs={24} sm={12} xl={6}><Statistic title="Beifahrerzeit" value={`${number(period.attendantHours)} h`} /></Col>
          </Row>
        </Card>
        <Card title="Gesamtübersicht · Musterwoche (Montag bis Freitag)">
          <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} items={[
            { key: "tours", label: "Touren", children: result.tours.length },
            { key: "km", label: "Strecke", children: `${number(summary.gesamtStreckeKm)} km` },
            { key: "hours", label: "Fahrtzeit", children: `${number(summary.gesamtFahrtzeitH)} h` },
            { key: "attendant", label: "Beifahrerzeit", children: `${number(summary.gesamtFahrtzeitBeifahrerH)} h` },
            { key: "busKm", label: "Fahrzeugkosten Strecke", children: euro(summary.kostenKmBus) },
            { key: "busH", label: "Fahrzeugkosten Zeit", children: euro(summary.kostenStundeBus) },
            { key: "attKm", label: "Beifahrerkosten Strecke", children: euro(summary.kostenKmBeifahrer) },
            { key: "attH", label: "Beifahrerkosten Zeit", children: euro(summary.kostenStundeBeifahrer) },
            { key: "cost", label: "Gesamtkosten", children: <b>{euro(summary.kostenGesamt)}</b> },
          ]} />
        </Card>
        <Card title="Downloads">
          <Space wrap>
            <Button icon={<DownloadOutlined />} loading={downloading === "/pdf-report"} onClick={() => download("/pdf-report", "routing-uebersicht.pdf")}>Gesamtübersicht (PDF)</Button>
            <Button icon={<DownloadOutlined />} loading={downloading === "/pdf-driver-all"} onClick={() => download("/pdf-driver-all", "fahrplaene.zip")}>Alle Fahrpläne (ZIP)</Button>
          </Space>
          <div style={{ marginTop: 12 }}><Space wrap>{(result.driverFiles || []).map((name, index) => <Button key={name} size="small" icon={<DownloadOutlined />} loading={downloading === `/saved-result/driver/${index}`} onClick={() => download(`/saved-result/driver/${index}`, name)}>{name}</Button>)}</Space></div>
        </Card>
        <Card title="Fahrpläne nach Bus · Musterwoche">
          <Space wrap style={{ marginBottom: 16 }}>
            <Input.Search placeholder="Fahrzeug, Depot oder Fahrgast suchen" value={query} onChange={event => setQuery(event.target.value)} allowClear style={{ width: 300, maxWidth: "100%" }} />
            <Select aria-label="Wochentag filtern" placeholder="Alle Wochentage" value={day} onChange={setDay} allowClear style={{ width: 180 }} options={Object.entries(DAYS).map(([value, label]) => ({ value, label }))} />
            <Select aria-label="Richtung filtern" placeholder="Beide Richtungen" value={direction} onChange={setDirection} allowClear style={{ width: 180 }} options={[{ value: "HIN", label: "Hinfahrt" }, { value: "RUECK", label: "Rückfahrt" }]} />
          </Space>
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            Pro Bus sind identische Fahrten zusammengefasst. Öffne einen Bus, um seine Fahrpläne zu sehen.
          </Text>
          {buses.length === 0 ? <Empty description="Keine Busse für diese Filter gefunden." /> :
            <Collapse activeKey={activeBusKeys} onChange={keys => setActiveBusKeys(Array.isArray(keys) ? keys : [keys])} items={buses.map(bus => ({
              key: bus.key,
              label: <Space wrap><Text strong>{bus.busName || `Bus Nr. ${bus.busId}`}</Text>
                <Text type="secondary" style={{ overflowWrap: "anywhere" }}>Depot / Parkort: {bus.depotName || "–"}</Text>
                <Text type="secondary">Kapazität: {bus.seats ?? "–"} Sitzplätze · {bus.wheelchairSeats ?? "–"} Rollstuhlplätze · {bus.electricWheelchairSeats ?? "–"} E-Rollstuhlplätze</Text>
                {changedBuses.includes(String(bus.busId)) ? <Tag color="orange">Manuell angepasst</Tag>
                  : bus.manuallyEdited === true ? <Tag color="blue">Manuell angepasst und neu berechnet</Tag>
                  : bus.manuallyEdited === false ? <Tag>Nicht manuell angepasst</Tag>
                  : <Tag>Bearbeitungsstatus nicht erfasst</Tag>}
                <Tag>{bus.patterns.length} {bus.patterns.length === 1 ? "Fahrplan" : "Fahrpläne"}</Tag>
              </Space>,
              children: <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <Text type="secondary">Depot: {bus.depotName || "–"}</Text>
                <Descriptions size="small" column={{ xs: 1, sm: 3 }} items={[
                  { key: "distance", label: "Strecke der angezeigten Woche", children: `${number(bus.tours.reduce((sum, tour) => sum + (tour.distanceKm || 0), 0))} km` },
                  { key: "duration", label: "Fahrtzeit", children: `${number(bus.tours.reduce((sum, tour) => sum + (tour.durationHours || 0), 0))} h` },
                  { key: "cost", label: "Kosten", children: euro(bus.tours.reduce((sum, tour) => sum + (tour.totalCost || 0), 0)) },
                ]} />
                {bus.patterns.map(tour => <div key={tour.key} ref={isTargetTour(tour) ? targetCard : null} style={{ scrollMarginTop: 24 }}>
                  <Card size="small" style={isTargetTour(tour) ? { borderColor: "#3FB6C2", boxShadow: "0 0 0 2px #3FB6C222" } : undefined}
                  title={<Space wrap><span style={{ whiteSpace: "normal" }}>{weekdays(tour.days)} · {tour.richtung === "HIN" ? "Hinfahrt" : "Rückfahrt"}</span>
                    {changedDirections.includes(`${tour.busId}:${tour.richtung}`) ? <Tag color="orange">Manuell angepasst</Tag>
                      : tour.directionManuallyEdited === true ? <Tag color="blue">Manuell angepasst und neu berechnet</Tag>
                      : tour.directionManuallyEdited === false ? <Tag>Nicht manuell angepasst</Tag>
                      : <Tag>Bearbeitungsstatus nicht erfasst</Tag>}
                    {isTargetTour(tour) && <Tag color="cyan">Ausgewählte Tour</Tag>}</Space>}
                  extra={<Space wrap><Tag color={tour.timeWindowFeasible === false ? "orange" : "green"}>{tour.timeWindowFeasible === false ? "Zeitfensterabweichung" : "Zeitfenster eingehalten"}</Tag>
                    <Link to={mapLink(tour)} aria-disabled={saving || restoring}
                      onClick={event => { if (saving || restoring) event.preventDefault(); }}><Button size="small" disabled={saving || restoring}>Auf Karte anzeigen</Button></Link>
                    <Button size="small" loading={editorLoading} disabled={saving || restoring} onClick={() => openEditor(tour)}>Bearbeiten</Button></Space>}>
                  <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }} items={[
                    { key: "time", label: "Fahrplan", children: `${time(tour.startTime)} – ${time(tour.endTime)} Uhr` },
                    { key: "distance", label: "Strecke je Fahrt", children: `${number(tour.distanceKm)} km` },
                    { key: "duration", label: "Dauer je Fahrt", children: `${number(tour.durationHours)} h` },
                    { key: "cost", label: "Kosten je Fahrt", children: euro(tour.totalCost) },
                  ]} />
                  {(tour.weeklyPlanningNotes || []).map(note => <div key={note} style={{ marginTop: 8 }}><Text type="secondary">{note}</Text></div>)}
                  <Table style={{ marginTop: 12 }} rowKey="sequenceIndex" columns={stopColumns}
                    dataSource={tour.stops || []} pagination={false} scroll={{ x: 800 }} />
                </Card></div>)}
              </Space>,
            }))} />}

        </Card>
      </>}
    {editor && <ManualTourEditor data={editor.data} initialTour={editor.initialTour} initialDraft={editor.draft}
      onClose={() => setEditor(null)} onAccept={draft => {
        setSession({ data: editor.data, draft }); setEditor(null); setAllowUnassigned(false); setSaveError("");
      }} />}
    <Modal open={restoreOpen} title="Optimierte Planung wiederherstellen?" okText="Wiederherstellen" cancelText="Abbrechen"
      confirmLoading={restoring} cancelButtonProps={{ disabled: restoring }} closable={!restoring} maskClosable={false}
      onCancel={() => { if (!restoring) setRestoreOpen(false); }} onOk={restoreOptimized}>
      Alle manuellen Anpassungen werden durch die letzte erfolgreich optimierte Planung ersetzt.
      {pending && <p>Auch deine noch nicht berechneten Entwürfe werden verworfen.</p>}
      <p>Die ursprünglichen Touren, Zeiten, Kosten, die Gesamtübersicht und die Downloads werden wiederhergestellt. Es findet keine neue Berechnung statt.</p>
      {saveError && <Alert type="error" showIcon message={saveError} />}
    </Modal>
    <Modal open={discardOpen} title="Alle gesammelten Änderungen verwerfen?" okText="Alle verwerfen" cancelText="Weiter bearbeiten"
      onCancel={() => setDiscardOpen(false)} onOk={() => {
        setSession(null); setDiscardOpen(false); setAllowUnassigned(false); setSaveError("");
      }}>
      Das gespeicherte Berechnungsergebnis bleibt erhalten. Alle noch nicht berechneten Anpassungen werden verworfen.
    </Modal>
  </Space>;
}
